import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { licensingApi } from "../api/licensingApi";
import type {
  EntitlementSnapshotResponse,
  MeterUsage,
} from "../types/licensing.types";
import { useAuth } from "./AuthProvider";
import { useOrganisationSwitch } from "./OrganisationSwitchProvider";

/**
 * The organisation's licence as the client sees it: which modules and
 * features the plan includes, how much of each meter is used, and whether
 * the subscription is blocked. Loaded once per organisation and refreshed
 * on demand (after a purchase, a plan change, or a 402 from any service).
 *
 * This only decides what the UI shows and offers. The backend's
 * entitlement engine is the boundary — every create/consume path checks
 * it again server-side.
 */
export interface LicensingContextType {
  /** null until the first load finishes (or fails). */
  snapshot: EntitlementSnapshotResponse | null;
  loading: boolean;
  /** Reload from the server. Safe to call often; requests are coalesced. */
  refresh: () => Promise<void>;
  /** Plan includes the module (or the org has no plan and is unrestricted). Unknown while loading. */
  hasModule: (moduleKey: string) => boolean;
  hasFeature: (featureKey: string) => boolean;
  featureLevel: (featureKey: string) => string | null;
  usageFor: (meterKey: string) => MeterUsage | undefined;
  /** True once the snapshot says the subscription is expired, suspended or cancelled. */
  isBlocked: boolean;
}

const LicensingContext = createContext<LicensingContextType | null>(null);

export const LicensingProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganisation } = useOrganisationSwitch();
  const [snapshot, setSnapshot] = useState<EntitlementSnapshotResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef<Promise<void> | null>(null);

  const load = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setSnapshot(null);
      return;
    }
    if (inFlight.current) return inFlight.current;
    setLoading(true);
    inFlight.current = licensingApi
      .snapshot()
      .then((s) => setSnapshot(s))
      .catch((err) => {
        // A licensing outage must never take the app down: without a
        // snapshot every gate stays open and the backend still enforces.
        console.warn("Licensing snapshot unavailable:", err?.message || err);
      })
      .finally(() => {
        setLoading(false);
        inFlight.current = null;
      });
    return inFlight.current;
  }, [isAuthenticated, user]);

  // Reload when the signed-in organisation changes (login, switch).
  useEffect(() => {
    void load();
  }, [load, currentOrganisation?.id]);

  // Any service may answer 402 when a limit is hit; the snapshot is then stale.
  useEffect(() => {
    const handler = () => void load();
    window.addEventListener("licensing:changed", handler);
    return () => window.removeEventListener("licensing:changed", handler);
  }, [load]);

  const value = useMemo<LicensingContextType>(() => {
    const hasModule = (key: string) => {
      if (!snapshot || snapshot.isUnrestricted) return true;
      const v = snapshot.modules?.[key];
      return v === undefined ? true : v;
    };
    const hasFeature = (key: string) => {
      if (!snapshot || snapshot.isUnrestricted) return true;
      const v = snapshot.features?.[key];
      return v === undefined ? true : v;
    };
    const featureLevel = (key: string) => snapshot?.featureLevels?.[key] ?? null;
    const usageFor = (key: string) => snapshot?.usage?.find((u) => u.key === key);
    return {
      snapshot,
      loading,
      refresh: load,
      hasModule,
      hasFeature,
      featureLevel,
      usageFor,
      isBlocked: !!snapshot?.isBlocked,
    };
  }, [snapshot, loading, load]);

  return <LicensingContext.Provider value={value}>{children}</LicensingContext.Provider>;
};

export const useLicensing = (): LicensingContextType => {
  const ctx = useContext(LicensingContext);
  if (!ctx) {
    throw new Error("useLicensing must be used within a LicensingProvider");
  }
  return ctx;
};

/** Tell the provider something changed licence-wise (purchase, plan change, 402). */
export const notifyLicensingChanged = () => {
  window.dispatchEvent(new Event("licensing:changed"));
};
